/* eslint-disable react-hooks/exhaustive-deps */
import React, { useContext, useRef, useEffect, useState } from 'react';
import { getDate, getMonth, getYear } from 'date-fns';
import { daysInMonth, isValid, getCappedUnits, DateUnits, twoDigitsNumber } from './utils/date';
import { upsert } from '../../../domain/answer/upsert';
import { MappedAnswer, SurveyPageContext } from '../../context/SurveyPageContext';
import { buildValueAnswer } from '../../../domain/answer/buildValueAnswer';
import DateInputProps from '../../../interfaces/dateInputProps';

import styles from './Date.module.css';

export enum Unit {
  day = 'day',
  month = 'month',
  year = 'year',
}

export const BASE_CLASS = 'date-input';

const BIRTH_DAY_QUESTION = 'What is your birthday?';
const DEFAULT_BIRTH_DAY = 1; // https://fffptc.atlassian.net/browse/RET-1850

export const DateInput: React.FunctionComponent<DateInputProps> = ({
  value,
  question,
  onBlur,
  dayPlaceholder = 'DD',
  monthPlaceholder = 'MM',
  yearPlaceholder = 'YYYY',
  label,
  disabled = false,
  autoTab = false,
}: DateInputProps) => {
  const dayInputRef = useRef<HTMLInputElement>(null);
  const monthInputRef = useRef<HTMLInputElement>(null);
  const yearInputRef = useRef<HTMLInputElement>(null);

  const isBirthdayQuestion = () => question.value === BIRTH_DAY_QUESTION;

  const getDay = (): number => {
    if (isBirthdayQuestion()) return DEFAULT_BIRTH_DAY;
    return getDate(value as Date);
  };

  const [parsedValues, setParsedValues] = useState<DateUnits>({
    day: getDay() || undefined,
    month: getMonth(value as Date) + 1 || undefined,
    year: getYear(value as Date) || undefined,
  });

  const { upsertMappedAnswers, mappedAnswers } = useContext(SurveyPageContext);

  useEffect(() => {
    const { day = undefined, month = undefined, year = undefined } = parsedValues;
    const isInitial =
      day === getDay() && month === getMonth(value as Date) + 1 && year === getYear(value as Date);

    if (
      !isInitial &&
      day !== undefined &&
      month !== undefined &&
      year !== undefined &&
      isValid(day, month, year) &&
      year.toString().length === 4
    ) {
      const newDate = new Date(year, month - 1, day);
      const questionId = question.id;
      const answer = buildValueAnswer({
        questionId,
        value: newDate.toISOString(),
      });
      const upsertedAnswers: Map<number, MappedAnswer> = upsert({
        answer,
        mappedAnswers,
      });
      upsertMappedAnswers(upsertedAnswers);
    }
  }, [parsedValues]);

  const handleFocus = ({ day, month, year }: DateUnits, inputId: Unit) => {
    const maxDays = month ? daysInMonth(month, year) : 31;

    if (inputId === Unit.day && parseInt(`${day}1`, 10) > maxDays) monthInputRef.current?.select();
    if (inputId === Unit.month && parseInt(`${month}1`, 10) > 12) yearInputRef.current?.select();
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>, inputId: Unit) => {
    const val = parseInt(e.target.value, 10);
    const newUnitValue: number | undefined = val >= 0 ? val : undefined;
    const newParsedValues: DateUnits = {
      ...parsedValues,
      [inputId]: newUnitValue,
    };
    const cappedValues: DateUnits = getCappedUnits(newParsedValues);

    if (autoTab) handleFocus(newParsedValues, inputId);
    setParsedValues(cappedValues);
  };

  const handleGroupBlur = (e: React.ChangeEvent<HTMLDivElement>) => {
    const { currentTarget } = e;

    setTimeout(() => {
      if (!currentTarget.contains(document.activeElement)) onBlur && onBlur(e);
    }, 0);
  };

  return (
    <div className={styles.dateContainer} data-testid={BASE_CLASS}>
      {label && (
        <label
          htmlFor={question.id.toString()}
          className={`${BASE_CLASS}-label`}
          data-testid={`${BASE_CLASS}-label`}
        >
          {label}
        </label>
      )}
      <div
        onBlur={handleGroupBlur}
        className={styles[`${BASE_CLASS}-inputs-wrapper`]}
        data-testid={`${BASE_CLASS}-inputs-wrapper`}
      >
        <input
          type="number"
          pattern="[0-9]*"
          placeholder={monthPlaceholder}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleChange(e, Unit.month)}
          value={twoDigitsNumber(parsedValues.month) || ''}
          className={styles.dateInput}
          ref={monthInputRef}
          disabled={disabled}
          maxLength={2}
        />
        {!isBirthdayQuestion() && (
          <input
            type="number"
            pattern="[0-9]*"
            placeholder={dayPlaceholder}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleChange(e, Unit.day)}
            value={twoDigitsNumber(parsedValues.day) || ''}
            className={styles.dateInput}
            ref={dayInputRef}
            disabled={disabled}
            maxLength={2}
          />
        )}
        <input
          type="number"
          pattern="[0-9]*"
          placeholder={yearPlaceholder}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleChange(e, Unit.year)}
          value={parsedValues.year || ''}
          className={styles.dateInput}
          ref={yearInputRef}
          disabled={disabled}
          maxLength={4}
        />
      </div>
    </div>
  );
};

DateInput.displayName = 'DateInput';

export default DateInput;
